import type { Unit } from "@stockline/shared/types/inventory/unit"
import { offlineDb } from "../../../lib/offline/db"
import { UNIT_TABLE } from "../../../lib/offline/resources/inventory/unit/db"
import { useSharedLiveQuery } from "../../../lib/offline/use-shared-live-query"

const EMPTY_UNITS: Unit[] = []

/**
 * Reactive global units list, shared across all consumers via
 * `useSharedLiveQuery`. Units rarely change at runtime, so once the list
 * has been resolved every `<UnitCell>` / `<PartnerButton>` / etc. reads
 * the same memoised array — remounting a recap card on scroll-back paints
 * instantly with the cached value, no IDB roundtrip.
 */
export function useUnits(): Unit[] {
  const rows = useSharedLiveQuery<Unit[]>("units:all", async () => {
    // `createdAt` isn't indexed on the units table — Dexie's `orderBy`
    // requires an index and would throw `SchemaError`, so we fetch and
    // sort in JS to match the legacy `reactive.useQuery` behaviour.
    const all = await offlineDb.table<Unit>(UNIT_TABLE).toArray()
    // Tombstone filter — delta-pulls upsert soft-deleted rows back
    // into Dexie with `deletedAt` set; hiding them here is what makes
    // an SSE `unit.deleted` visibly remove the unit from selectors.
    const live = all.filter(
      (u) => !(u as Unit & { deletedAt?: string | null }).deletedAt
    )
    return live.sort((a, b) => {
      const ac = (a as Unit & { createdAt?: string | null }).createdAt
      const bc = (b as Unit & { createdAt?: string | null }).createdAt
      if (ac == null || bc == null) return 0
      return ac < bc ? -1 : ac > bc ? 1 : 0
    })
  })
  return rows ?? EMPTY_UNITS
}
